/**
 * Braze Session Start
 *
 * bowflex-dtc and schwinnfitness share one Braze workspace, and storefront
 * context isn't exposed at the SDK's own session start — so this fires a
 * `store_session_started` custom event as a store-aware proxy for it. Its
 * `brand` property (merged in by logCustomEventOrThrow, see util.braze.js) is
 * what a Canvas or segment can filter on to tell the two stores apart, in
 * place of the native "start session" trigger, which has no per-store filter.
 *
 * That swap — pointing a campaign's trigger, or a segment's membership
 * filter, at this custom event instead of native session-start — happens in
 * the Braze dashboard, not in this repo, and what a specific campaign/Canvas
 * actually does with each firing (frequency capping, re-entry rules, other
 * audience filters) is dashboard-side and outside this repo's visibility —
 * this file has no way to know or control that. What it can control is not
 * needlessly inflating how often the event fires, so it's kept to once per
 * real Braze session rather than once per page load.
 *
 * "Once per real Braze session" is possible because the Web SDK documents
 * (though doesn't fully spell out the value contract of) a first-party
 * `ab.storage.sessionId.[api-key]` cookie: "Randomly-generated string used to
 * determine whether the user is starting a new or existing session." Its
 * value is the same `session_id` every event payload carries — confirmed
 * against a live network capture, not assumed from the doc text alone — so
 * diffing it directly tells us when Braze itself considers a new session to
 * have started, without needing to know or mirror its configured timeout
 * (checked live: this integration's cookie shows ~31.4 min between its `c:`
 * and `e:` timestamps, not the documented 30 min default — a value we'd have
 * gotten wrong by guessing). It also catches every way Braze can open a new
 * session, not just its timeout: `braze.changeUser()` with a new ID — which
 * bfx-braze-lead-capture.js already calls on every submission — ends the
 * current session and opens a real new one, which a hand-rolled inactivity
 * timer would have no way to observe but this cookie reflects directly. That
 * catch happens on the next page load, not in-page — this module reads the
 * cookie once when it evaluates, so a `changeUser()` mid-page doesn't fire a
 * second event until the visitor navigates or reloads.
 *
 * The check runs inside sendToBraze's `send` callback rather than gating the
 * call to sendToBraze itself (unlike bfx-exit-intent.js's buildPayload(),
 * which decides before calling sendToBraze) — deliberately, not an
 * inconsistency: the cookie is written by the app embed's own init routine,
 * so reading it before SDK readiness is confirmed risks a stale or absent
 * value. bfx-exit-intent.js's pre-check has no such dependency, since it
 * only reads Shopify's own /cart.js.
 *
 * SDK readiness is handled by the shared util.braze helper.
 */

import { logCustomEventOrThrow, sendToBraze } from 'util.braze';

const EVENT_NAME = 'store_session_started';
const LAST_SESSION_ID_KEY = 'bfx-braze-session-last-session-id';

// Cookie value shape: g:<session-guid>|e:<expiry-ms>|c:<created-ms>|l:<last-active-ms>.
// Only `g` (the session id itself) is needed here; the rest is Braze's own bookkeeping.
const currentBrazeSessionId = () => {
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith('ab.storage.sessionId.'));
  if (!cookie) return null;

  return decodeURIComponent(cookie.split('=')[1] ?? '').match(/(?:^|\|)g:([^|]+)/)?.[1] ?? null;
};

sendToBraze(
  () => {
    const sessionId = currentBrazeSessionId();
    if (!sessionId) {
      console.warn(
        'bfx-braze-session: could not read a Braze session id (cookie missing or unparsable)'
      );
      return;
    }
    if (sessionId === localStorage.getItem(LAST_SESSION_ID_KEY)) return;

    logCustomEventOrThrow(EVENT_NAME);
    localStorage.setItem(LAST_SESSION_ID_KEY, sessionId);
  },
  `bfx-braze-session: ${EVENT_NAME}`,
  'bfx-braze-session'
);
